import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { Link, useLocation } from 'react-router-dom'

export interface TabItem {
  label: string
  path: string
  disabled?: boolean
  id?: string
  isActive?: (currentPath: string) => boolean
  /**
   * In-page tab: selecting it flips local state instead of navigating. When
   * set the tab renders as a button and `isActive` decides its state.
   */
  onSelect?: () => void
  count?: number
  badge?: 'beta' | 'coming-soon' | 'new'
}

interface PageTabsProps {
  tabs: TabItem[]
  className?: string
}

export function PageTabs({ tabs, className }: PageTabsProps) {
  const location = useLocation()
  const currentPath = location.pathname

  return (
    <div
      className={cn(
        'scrollbar-thin scrollbar-thumb-border flex w-full min-w-0 max-w-full space-x-1 overflow-x-auto overflow-y-hidden sm:space-x-4',
        'overscroll-x-contain [-webkit-overflow-scrolling:touch]',
        className,
      )}
    >
      {tabs.map(tab => {
        // Determine if tab is active using the custom function if provided
        // Otherwise fall back to simple path matching
        const isActive = tab.isActive
          ? tab.isActive(currentPath)
          : currentPath === tab.path || currentPath.endsWith(tab.path)

        // Regular tab without dropdown. Tabs that carry an `onSelect`
        // switch in-page state (e.g. My Work's work-type lens) and render as
        // a button; the rest keep the Link + path behaviour.
        const tabClassName = cn(
          'text-consistent shrink-0 whitespace-nowrap px-2 py-3 text-sm font-medium transition-colors sm:px-4',
          'flex items-center gap-2 border-b-2',
          isActive
            ? 'border-primary text-foreground'
            : 'border-transparent text-muted-foreground hover:border-muted-foreground/30 hover:text-foreground',
          tab.disabled && 'pointer-events-none cursor-not-allowed opacity-50',
        )

        const tabContent = (
          <>
            <span>{tab.label}</span>
            {tab.badge && (
              <Badge
                variant={
                  tab.badge === 'beta'
                    ? 'secondary'
                    : tab.badge === 'new'
                      ? 'success'
                      : 'outline'
                }
                className={cn(
                  'h-5 px-1.5 text-[10px] font-medium uppercase tracking-wide',
                  tab.badge === 'beta' &&
                    'border-amber-500/30 bg-amber-500/15 text-amber-600 dark:text-amber-400',
                  tab.badge === 'coming-soon' &&
                    'bg-muted text-muted-foreground',
                  tab.badge === 'new' &&
                    'border-emerald-500/30 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400',
                )}
              >
                {tab.badge === 'coming-soon' ? 'Soon' : tab.badge}
              </Badge>
            )}
            {typeof tab.count === 'number' && tab.count > 0 && (
              <Badge
                variant="secondary"
                className="h-5 border-0 bg-muted/60 px-1.5 text-xs font-normal text-muted-foreground"
              >
                {tab.count.toLocaleString()}
              </Badge>
            )}
          </>
        )

        if (tab.onSelect) {
          return (
            <button
              key={tab.id || tab.path}
              type="button"
              role="tab"
              aria-selected={isActive}
              className={tabClassName}
              disabled={tab.disabled}
              onClick={tab.onSelect}
            >
              {tabContent}
            </button>
          )
        }

        return (
          <Link
            key={tab.path}
            to={tab.disabled ? '#' : tab.path}
            className={tabClassName}
            onClick={e => {
              if (tab.disabled) {
                e.preventDefault()
              }
            }}
          >
            {tabContent}
          </Link>
        )
      })}
    </div>
  )
}
